// src/pages/help/topics/copyTrading.js
// Guide « Copy Trading ». Contenu validé — toute modification se fait ici.
export const fr = {
  title: "Copy Trading",
  tagline: "Reproduire les positions d'un compte Master",
  readMin: 9,
  intro:
    "Le Copy Trading reproduit automatiquement les positions d'un compte MetaTrader 5 principal vers un ou plusieurs autres comptes MT5. Il repose sur deux types de comptes : le compte Master, qui envoie les positions, et les comptes copieurs, qui les reçoivent et les reproduisent. Quand le Master ouvre, modifie ou ferme une position, l'action est transmise aux comptes copieurs connectés.",
  brief: [
    "La synchronisation va dans un seul sens : Master → Copieurs",
    "Agent FullMetrix déjà installé sur un compte = compte Master déjà prêt",
    "Un Agent copieur et un token différent par compte receveur",
    "Le volume copié se limite compte par compte, pour adapter le risque",
  ],
  steps: [
    {
      title: "Installer l'Agent principal sur le compte Master",
      text: "Depuis la section Agent IA. Dès que cet Agent est installé et connecté sur un compte MT5, ce compte devient automatiquement un compte Master : il n'y a pas d'autre Agent principal à installer depuis la section Copy Trading.",
    },
    {
      title: "Choisir le mode public ou privé",
      text: "Master public : le profil est visible par les autres utilisateurs, qui peuvent le consulter et obtenir un accès pour copier ses positions. Master privé : le profil n'apparaît nulle part et le propriétaire crée lui-même les accès, pour ses propres comptes, ses clients ou des personnes de confiance.",
    },
    {
      title: "Préparer chaque compte copieur",
      text: "Dans MT5 : Outils → Options → Conseillers Experts. Trading algorithmique activé, WebRequest autorisées, adresse https://ia.fullmargin.net ajoutée à la liste. MT5 ouvert, compte connecté, machine en ligne. Ces réglages se font une seule fois.",
    },
    {
      title: "Installer l'Agent copieur",
      text: "Télécharge l'Agent copieur depuis la section Copy Trading, ajoute-le à MT5, ouvre un graphique, dépose l'Agent dessus, puis vérifie qu'il est actif. Il peut être installé sur plusieurs comptes MT5, même chez des brokers différents, sous réserve de compatibilité.",
    },
    {
      title: "Générer un token pour chaque compte copieur",
      text: "Chaque Agent copieur a besoin d'un token pour savoir quel Master suivre. Crée un accès, nomme-le (Compte personnel, Prop Firm 1, Compte démo, Client VIP, VPS 1), génère le token, copie-le et colle-le dans l'Agent copieur correspondant.",
    },
    {
      title: "Régler le volume autorisé",
      text: "L'Agent copieur limite le volume des positions copiées. C'est utile dès que le compte copieur dispose de moins de capital que le Master.",
    },
    {
      title: "Vérifier puis tester avec un petit volume",
      text: "Agent principal et Agents copieurs actifs, tokens correctement renseignés, trading algorithmique et WebRequest en place, comptes connectés, limites de lot adaptées. Commence petit pour vérifier l'ouverture, le volume copié, le Stop Loss, le Take Profit, les modifications et la fermeture.",
    },
  ],
  sections: [
    {
      title: "Régler le volume autorisé",
      text: "Le lot copié n'est pas forcément celui du Master : chaque Agent copieur applique ses propres bornes.",
      bullets: [
        "Lot maximal — le Master ouvre 10 lots, le copieur est limité à 0,01 lot : la position copiée s'ouvre à 0,01 lot.",
        "Lot minimal — le copieur n'accepte que les positions d'au moins 0,20 lot : une position transmise en dessous de cette limite n'est pas ouverte.",
        "Les réglages s'adaptent au capital, au levier, aux règles du broker, aux règles de la prop firm et au niveau de risque souhaité.",
      ],
    },
    {
      title: "Conditions indispensables pour que la copie fonctionne",
      text: "Les Agents fonctionnent dans la version ordinateur de MetaTrader 5.",
      bullets: [
        "MT5 doit rester ouvert et connecté. Ordinateur éteint, MT5 fermé ou connexion coupée : la copie ne se fait pas. Si le Master ouvre une position pendant qu'un copieur est hors ligne, ce copieur ne la reçoit pas.",
        "Le trading algorithmique doit rester activé, sur le Master comme sur chaque copieur, sinon les ordres ne sont ni transmis ni exécutés correctement.",
        "L'adresse https://ia.fullmargin.net doit rester autorisée dans les WebRequest. Absente ou incorrecte, l'Agent ne peut pas communiquer avec FullMetrix.",
      ],
      note: "MetaTrader sur téléphone ne suffit pas à faire fonctionner l'Agent.",
    },
    {
      title: "Prendre ses positions depuis le téléphone",
      text: "Tu peux passer un ordre depuis l'application mobile MT5 et la copie fonctionnera, à condition que l'ordinateur fasse le travail :",
      bullets: [
        "le même compte est également ouvert sur MT5 ordinateur",
        "l'Agent principal est actif sur cet ordinateur",
        "l'ordinateur reste allumé",
        "MT5 reste ouvert et connecté à Internet",
      ],
      note: "Le téléphone sert à passer les ordres ; l'Agent, lui, continue de tourner depuis l'ordinateur.",
    },
    {
      title: "Faire tourner le tout sur un VPS",
      text: "Un VPS garde MT5 et les Agents actifs en permanence, même ordinateur personnel éteint. Pour une copie continue, on y installe :",
      bullets: [
        "MT5 du compte Master",
        "l'Agent principal",
        "MT5 des comptes copieurs",
        "les Agents copieurs",
      ],
      note: "Tant que le VPS tourne et reste connecté, les positions continuent d'être copiées. C'est la solution recommandée pour un fonctionnement permanent.",
    },
    {
      title: "Utiliser d'autres robots sur le compte Master",
      text: "Le compte Master peut faire tourner plusieurs robots ou Expert Advisors en même temps. Une position ouverte par un autre robot, sur un autre graphique du compte Master, peut être transmise aux comptes copieurs : le Copy Trading ne copie pas uniquement les ordres passés à la main.",
    },
    {
      title: "Ce qui est synchronisé",
      text: "Quand une position vient du Master, les éléments suivants peuvent être transmis aux copieurs :",
      bullets: [
        "ouverture de la position",
        "fermeture de la position",
        "modification du Stop Loss",
        "modification du Take Profit",
        "autres modifications prises en charge par l'Agent",
      ],
      note: "Quand le Master ferme une position, elle est également fermée sur les comptes copieurs concernés.",
    },
    {
      title: "Ce qui ne remonte pas vers le Master",
      text: "Un compte copieur peut modifier ou fermer localement une position copiée. Cette action ne modifie ni la position du Master, ni celles des autres comptes copieurs. La synchronisation reste toujours Master → Copieur.",
    },
    {
      title: "Installer les deux Agents sur le même compte",
      text: "Il est possible de poser l'Agent principal sur un graphique et l'Agent copieur sur un autre graphique du même compte. Ce compte devient alors à la fois Master et copieur.",
      note: "Une position ouverte par le Master peut alors être ouverte une seconde fois sur le même compte par l'Agent copieur. Cette configuration duplique les positions : à utiliser avec prudence.",
    },
    {
      title: "Gérer les accès copieurs",
      text: "Depuis le panneau Copy Trading, le propriétaire du Master peut :",
      bullets: [
        "créer plusieurs tokens",
        "nommer chaque accès et identifier chaque compte",
        "consulter les comptes actifs",
        "désactiver un copieur ou révoquer un token",
        "interrompre la copie pour un compte précis",
      ],
      note: "Un accès désactivé ne reçoit plus les nouveaux ordres du Master.",
    },
    {
      title: "Résumé rapide",
      bullets: [
        "Installer l'Agent principal sur le compte Master.",
        "Choisir un profil public ou privé.",
        "Préparer chaque compte copieur dans MT5.",
        "Installer un Agent copieur par compte.",
        "Générer un token différent pour chaque compte.",
        "Régler les limites de volume.",
        "Garder MT5 ouvert, connecté et actif.",
        "Tester avec un petit volume.",
        "Utiliser un VPS pour un fonctionnement permanent.",
      ],
      note: "Le Master envoie les positions, les copieurs les reçoivent, et tous les Agents doivent rester actifs et connectés.",
    },
  ],
  tips: [
    "Renomme tes tokens copieurs (Copieur compte personnel, Copieur Prop Firm 1, Copieur compte démo…) pour t'y retrouver.",
    "Chaque Agent copieur peut limiter le volume maximal par position (0,01 lot, 0,10 lot, etc.) pour adapter le risque au capital et aux règles du compte.",
    "Les résultats ne sont pas forcément identiques d'un compte à l'autre : spread, slippage, broker, symbole et volume jouent.",
  ],
  faq: [
    {
      q: "Qu'est-ce que le Copy Trading ?",
      a:
        "Le Copy Trading permet de reproduire automatiquement les positions d'un compte MT5 principal vers un ou plusieurs autres comptes.\n\n" +
        "Il fonctionne avec deux types d'Agents :\n" +
        "• l'Agent principal, installé sur le compte Master\n" +
        "• l'Agent copieur, installé sur chaque compte qui doit recevoir les positions",
    },
    {
      q: "Comment un compte devient-il un compte Master ?",
      a:
        "Lorsqu'un utilisateur installe l'Agent principal FullMetrix sur son compte MetaTrader 5 depuis la section Agent IA, ce compte peut ensuite être utilisé comme compte Master dans la partie Copy Trading.\n\n" +
        "Les positions prises sur ce compte pourront alors être reproduites sur les comptes copieurs connectés.",
    },
    {
      q: "Quelles sont les deux manières d'utiliser le Copy Trading ?",
      a:
        "L'utilisateur peut utiliser le Copy Trading de deux façons :\n\n" +
        "1. Rendre son compte Master public\n" +
        "Il crée un profil public afin que d'autres utilisateurs FullMetrix puissent retrouver son compte Master et s'y connecter.\n\n" +
        "2. Utiliser le Copy Trading pour ses propres comptes\n" +
        "Il garde son compte Master privé et connecte uniquement les comptes MT5 sur lesquels il souhaite reproduire ses propres positions.",
    },
    {
      q: "Comment fonctionne un compte Master public ?",
      a:
        "L'utilisateur crée un profil dans la section Copy Trading et le rend public.\n\n" +
        "Les autres utilisateurs FullMetrix peuvent alors :\n\n" +
        "1. retrouver son profil\n" +
        "2. cliquer sur Se connecter\n" +
        "3. générer un token copieur\n" +
        "4. télécharger l'Agent copieur\n" +
        "5. installer l'Agent copieur sur leur compte MT5\n" +
        "6. coller le token généré dans l'Agent copieur\n\n" +
        "Une fois la connexion active, les positions du compte Master peuvent être reproduites sur le compte de l'utilisateur connecté.",
    },
    {
      q: "Comment copier ses positions sur ses propres comptes ?",
      a:
        "Depuis la partie Copy Trading du compte Master :\n\n" +
        "1. Télécharge l'Agent copieur.\n" +
        "2. Installe-le sur chaque compte MT5 qui doit recevoir les positions.\n" +
        "3. Génère un token copieur différent pour chaque compte.\n" +
        "4. Copie chaque token.\n" +
        "5. Colle le bon token dans l'Agent copieur du compte correspondant.\n" +
        "6. Vérifie que tous les Agents sont actifs.\n\n" +
        "Les positions prises sur le compte Master seront ensuite reproduites sur les comptes copieurs connectés.",
    },
    {
      q: "Que faut-il préparer sur chaque compte copieur ?",
      a:
        "Avant d'installer l'Agent copieur sur un compte MT5, il faut :\n" +
        "• activer le trading algorithmique\n" +
        "• autoriser les requêtes WebRequest\n" +
        "• ajouter l'adresse https://ia.fullmargin.net dans les URL autorisées\n" +
        "• installer l'Agent copieur sur un graphique\n" +
        "• renseigner un token copieur valide\n" +
        "• laisser MetaTrader 5 ouvert et connecté à Internet\n\n" +
        "Ces étapes doivent être respectées sur chaque compte copieur.",
    },
    {
      q: "Faut-il utiliser un token différent pour chaque compte copieur ?",
      a:
        "Oui, c'est recommandé.\n\n" +
        "Chaque compte copieur doit avoir son propre token afin de :\n" +
        "• l'identifier facilement\n" +
        "• gérer sa connexion séparément\n" +
        "• éviter les confusions\n" +
        "• contrôler précisément les comptes actifs\n\n" +
        "Il est conseillé de renommer les tokens selon les comptes concernés.\n\n" +
        "Exemples :\n" +
        "• Copieur compte personnel\n" +
        "• Copieur Prop Firm 1\n" +
        "• Copieur Prop Firm 2\n" +
        "• Copieur compte démo",
    },
    {
      q: "Peut-on limiter le lot sur un compte copieur ?",
      a:
        "Oui.\n\n" +
        "Chaque Agent copieur peut être configuré afin de limiter le volume maximal autorisé par position.\n\n" +
        "Par exemple, même si le compte Master ouvre une position avec un lot élevé, le compte copieur peut être réglé pour ne pas dépasser :\n" +
        "• 0,01 lot\n" +
        "• 0,10 lot\n" +
        "• ou toute autre limite autorisée\n\n" +
        "Cela permet d'adapter le risque au capital et aux règles de chaque compte copieur.",
    },
    {
      q: "Les positions sont-elles copiées automatiquement ?",
      a:
        "Oui, lorsque tous les Agents sont correctement configurés et connectés.\n\n" +
        "Une position prise sur le compte Master peut être reproduite sur les comptes copieurs associés.\n\n" +
        "La copie dépend toutefois de plusieurs éléments :\n" +
        "• MetaTrader 5 doit rester ouvert\n" +
        "• les Agents doivent rester actifs\n" +
        "• les tokens doivent être valides\n" +
        "• le trading algorithmique doit être activé\n" +
        "• les comptes doivent disposer d'une connexion Internet\n" +
        "• le broker doit accepter le symbole et le volume demandés",
    },
    {
      q: "Pourquoi une position peut-elle ne pas être copiée ?",
      a:
        "Vérifie notamment :\n" +
        "• que le compte Master est connecté\n" +
        "• que l'Agent principal est actif\n" +
        "• que l'Agent copieur est installé sur le bon compte\n" +
        "• que le token copieur est correct\n" +
        "• que le trading algorithmique est activé\n" +
        "• que le WebRequest est toujours autorisé\n" +
        "• que MetaTrader 5 est ouvert\n" +
        "• que le volume choisi est accepté\n" +
        "• que le compte possède une marge suffisante\n" +
        "• que le symbole existe chez le broker du compte copieur",
    },
    {
      q: "Les résultats seront-ils exactement identiques ?",
      a:
        "Pas forcément.\n\n" +
        "Des différences peuvent apparaître en fonction :\n" +
        "• du spread\n" +
        "• du slippage\n" +
        "• du broker\n" +
        "• du symbole disponible\n" +
        "• du volume configuré\n" +
        "• de la vitesse de connexion\n" +
        "• des règles du compte ou de la prop firm\n\n" +
        "Le Copy Trading transmet les positions, mais ne garantit pas un résultat parfaitement identique sur tous les comptes.",
    },
    {
      q: "Quelles précautions faut-il prendre ?",
      a:
        "Avant d'activer la copie, vérifie toujours :\n" +
        "• le bon compte Master\n" +
        "• les bons comptes copieurs\n" +
        "• les tokens utilisés\n" +
        "• le lot maximal autorisé\n" +
        "• la marge disponible\n" +
        "• les règles de chaque broker ou prop firm\n" +
        "• l'état de connexion de tous les Agents\n\n" +
        "Il est recommandé de commencer avec un petit volume afin de vérifier que toute la configuration fonctionne correctement.",
    },
    {
      q: "Comment contacter le support ?",
      a:
        "Pour obtenir une réponse plus rapide, nous recommandons de contacter le support FullMargin par WhatsApp.\n\n" +
        "En cas de problème, envoie :\n" +
        "• l'adresse e-mail du compte FullMargin\n" +
        "• le compte Master concerné\n" +
        "• les comptes copieurs concernés\n" +
        "• le nom des tokens utilisés\n" +
        "• une capture des Agents\n" +
        "• une capture de la configuration Copy Trading\n" +
        "• le symbole et l'heure de la position non copiée",
    },
  ],
  related: ["agent-ia-mt5", "execution", "autres"],
};

export const en = {
  title: "Copy Trading",
  tagline: "Mirror the positions of a Master account",
  readMin: 9,
  intro:
    "Copy Trading automatically mirrors the positions of a main MetaTrader 5 account onto one or several other MT5 accounts. It relies on two kinds of accounts: the Master account, which sends the positions, and the copier accounts, which receive and reproduce them. When the Master opens, changes or closes a position, the action is forwarded to the connected copier accounts.",
  brief: [
    "Syncing goes one way only: Master → copiers",
    "A FullMetrix Agent already installed on an account = a Master account already set",
    "One copier Agent and one distinct token per receiving account",
    "Copied volume is capped account by account, to match the risk",
  ],
  steps: [
    {
      title: "Install the main Agent on the Master account",
      text: "From the AI Agent section. As soon as that Agent is installed and connected on an MT5 account, the account automatically becomes a Master account: there is no second main Agent to install from the Copy Trading section.",
    },
    {
      title: "Choose public or private mode",
      text: "Public Master: the profile is visible to other users, who can review it and request access to copy its positions. Private Master: the profile stays hidden and the owner creates the accesses, for their own accounts, their clients or trusted people.",
    },
    {
      title: "Prepare each copier account",
      text: "In MT5: Tools → Options → Expert Advisors. Algorithmic trading enabled, WebRequest allowed, https://ia.fullmargin.net added to the list. MT5 open, account connected, machine online. These settings are done once.",
    },
    {
      title: "Install the copier Agent",
      text: "Download the copier Agent from the Copy Trading section, add it to MT5, open a chart, drop the Agent on it, then check that it is running. It can be installed on several MT5 accounts, even across different brokers, subject to compatibility.",
    },
    {
      title: "Generate a token for each copier account",
      text: "Every copier Agent needs a token to know which Master to follow. Create an access, name it (Personal account, Prop Firm 1, Demo account, VIP client, VPS 1), generate the token, copy it and paste it into the matching copier Agent.",
    },
    {
      title: "Set the allowed volume",
      text: "The copier Agent caps the volume of copied positions. This matters as soon as the copier account holds less capital than the Master.",
    },
    {
      title: "Check, then test with a small volume",
      text: "Main Agent and copier Agents running, tokens correctly filled in, algorithmic trading and WebRequest in place, accounts connected, lot limits sensible. Start small to check the opening, the copied volume, the Stop Loss, the Take Profit, the changes and the closing.",
    },
  ],
  sections: [
    {
      title: "Setting the allowed volume",
      text: "The copied lot is not necessarily the Master's: each copier Agent applies its own bounds.",
      bullets: [
        "Maximum lot — the Master opens 10 lots, the copier is capped at 0.01 lot: the copied position opens at 0.01 lot.",
        "Minimum lot — the copier only accepts positions of at least 0.20 lot: a smaller forwarded position is not opened.",
        "Settings should match the capital, the leverage, the broker rules, the prop firm rules and the risk level you want.",
      ],
    },
    {
      title: "What copying requires to work",
      text: "The Agents run in the desktop version of MetaTrader 5.",
      bullets: [
        "MT5 must stay open and connected. Computer off, MT5 closed or connection lost: no copying. If the Master opens a position while a copier is offline, that copier does not receive it.",
        "Algorithmic trading must stay enabled, on the Master as well as on every copier, otherwise orders are neither forwarded nor executed properly.",
        "https://ia.fullmargin.net must stay allowed under WebRequest. Missing or wrong, the Agent cannot talk to FullMetrix.",
      ],
      note: "MetaTrader on a phone is not enough to keep the Agent running.",
    },
    {
      title: "Taking positions from your phone",
      text: "You can place an order from the MT5 mobile app and copying will work, as long as the computer does the job:",
      bullets: [
        "the same account is also open in desktop MT5",
        "the main Agent is running on that computer",
        "the computer stays on",
        "MT5 stays open and connected to the internet",
      ],
      note: "The phone places the orders; the Agent keeps running from the computer.",
    },
    {
      title: "Running everything on a VPS",
      text: "A VPS keeps MT5 and the Agents running around the clock, even with your own computer off. For continuous copying, install on it:",
      bullets: [
        "MT5 of the Master account",
        "the main Agent",
        "MT5 of the copier accounts",
        "the copier Agents",
      ],
      note: "As long as the VPS runs and stays connected, positions keep being copied. This is the recommended setup for permanent operation.",
    },
    {
      title: "Using other robots on the Master account",
      text: "The Master account can run several robots or Expert Advisors at the same time. A position opened by another robot, on another chart of the Master account, can be forwarded to the copier accounts: Copy Trading does not only copy orders placed by hand.",
    },
    {
      title: "What gets synced",
      text: "When a position comes from the Master, the following can be forwarded to the copiers:",
      bullets: [
        "opening the position",
        "closing the position",
        "Stop Loss changes",
        "Take Profit changes",
        "other changes supported by the Agent",
      ],
      note: "When the Master closes a position, it is closed on the copier accounts concerned as well.",
    },
    {
      title: "What never travels back to the Master",
      text: "A copier account can change or close a copied position locally. That action changes neither the Master's position nor those of the other copier accounts. Syncing always stays Master → copier.",
    },
    {
      title: "Installing both Agents on the same account",
      text: "You can put the main Agent on one chart and the copier Agent on another chart of the same account. That account then acts as both Master and copier.",
      note: "A position opened by the Master can then be opened a second time on the same account by the copier Agent. This setup duplicates positions: use it with care.",
    },
    {
      title: "Managing copier accesses",
      text: "From the Copy Trading panel, the Master owner can:",
      bullets: [
        "create several tokens",
        "name each access and identify each account",
        "review the active accounts",
        "disable a copier or revoke a token",
        "stop copying for one specific account",
      ],
      note: "A disabled access no longer receives new orders from the Master.",
    },
    {
      title: "Quick recap",
      bullets: [
        "Install the main Agent on the Master account.",
        "Choose a public or private profile.",
        "Prepare each copier account in MT5.",
        "Install one copier Agent per account.",
        "Generate a different token for each account.",
        "Set the volume limits.",
        "Keep MT5 open, connected and running.",
        "Test with a small volume.",
        "Use a VPS for permanent operation.",
      ],
      note: "The Master sends the positions, the copiers receive them, and every Agent must stay active and connected.",
    },
  ],
  tips: [
    "Rename your copier tokens (Personal account copier, Prop Firm 1 copier, Demo copier…) to find your way around.",
    "Each copier Agent can cap the maximum volume per position (0.01 lot, 0.10 lot, etc.) to match the capital and rules of that account.",
    "Results are not necessarily identical across accounts: spread, slippage, broker, symbol and volume all matter.",
  ],
  faq: [
    {
      q: "What is Copy Trading?",
      a:
        "Copy Trading automatically mirrors the positions of a main MT5 account onto one or several other accounts.\n\n" +
        "It works with two kinds of Agents:\n" +
        "• the main Agent, installed on the Master account\n" +
        "• the copier Agent, installed on every account that must receive the positions",
    },
    {
      q: "How does an account become a Master account?",
      a:
        "When you install the FullMetrix main Agent on your MetaTrader 5 account from the AI Agent section, that account can then be used as a Master account in Copy Trading.\n\n" +
        "Positions taken on it can then be mirrored onto the connected copier accounts.",
    },
    {
      q: "What are the two ways to use Copy Trading?",
      a:
        "You can use Copy Trading in two ways:\n\n" +
        "1. Make your Master account public\n" +
        "You create a public profile so other FullMetrix users can find your Master account and connect to it.\n\n" +
        "2. Use Copy Trading for your own accounts\n" +
        "You keep your Master account private and only connect the MT5 accounts where you want to mirror your own positions.",
    },
    {
      q: "How does a public Master account work?",
      a:
        "You create a profile in the Copy Trading section and make it public.\n\n" +
        "Other FullMetrix users can then:\n\n" +
        "1. find your profile\n" +
        "2. click Connect\n" +
        "3. generate a copier token\n" +
        "4. download the copier Agent\n" +
        "5. install the copier Agent on their MT5 account\n" +
        "6. paste the generated token into the copier Agent\n\n" +
        "Once the connection is active, the Master account positions can be mirrored onto the connected user's account.",
    },
    {
      q: "How do I copy my positions onto my own accounts?",
      a:
        "From the Copy Trading section of the Master account:\n\n" +
        "1. Download the copier Agent.\n" +
        "2. Install it on every MT5 account that must receive the positions.\n" +
        "3. Generate a different copier token for each account.\n" +
        "4. Copy each token.\n" +
        "5. Paste the right token into the copier Agent of the matching account.\n" +
        "6. Check that all Agents are active.\n\n" +
        "Positions taken on the Master account are then mirrored onto the connected copier accounts.",
    },
    {
      q: "What must be prepared on each copier account?",
      a:
        "Before installing the copier Agent on an MT5 account you must:\n" +
        "• enable algorithmic trading\n" +
        "• allow WebRequest calls\n" +
        "• add https://ia.fullmargin.net to the allowed URLs\n" +
        "• install the copier Agent on a chart\n" +
        "• fill in a valid copier token\n" +
        "• keep MetaTrader 5 open and connected to the internet\n\n" +
        "These steps must be done on every copier account.",
    },
    {
      q: "Should I use a different token for each copier account?",
      a:
        "Yes, that is recommended.\n\n" +
        "Each copier account should have its own token so you can:\n" +
        "• identify it easily\n" +
        "• manage its connection separately\n" +
        "• avoid confusion\n" +
        "• control exactly which accounts are active\n\n" +
        "Rename the tokens after the accounts they belong to.\n\n" +
        "Examples:\n" +
        "• Personal account copier\n" +
        "• Prop Firm 1 copier\n" +
        "• Prop Firm 2 copier\n" +
        "• Demo account copier",
    },
    {
      q: "Can I cap the lot on a copier account?",
      a:
        "Yes.\n\n" +
        "Each copier Agent can be configured to cap the maximum volume allowed per position.\n\n" +
        "For example, even if the Master account opens a position with a large lot, the copier account can be set never to exceed:\n" +
        "• 0.01 lot\n" +
        "• 0.10 lot\n" +
        "• or any other allowed limit\n\n" +
        "This adapts the risk to the capital and rules of each copier account.",
    },
    {
      q: "Are positions copied automatically?",
      a:
        "Yes, when all Agents are properly configured and connected.\n\n" +
        "A position taken on the Master account can be mirrored onto the associated copier accounts.\n\n" +
        "Copying still depends on several things:\n" +
        "• MetaTrader 5 must stay open\n" +
        "• the Agents must stay active\n" +
        "• the tokens must be valid\n" +
        "• algorithmic trading must be enabled\n" +
        "• the accounts must have an internet connection\n" +
        "• the broker must accept the requested symbol and volume",
    },
    {
      q: "Why might a position not be copied?",
      a:
        "Check:\n" +
        "• that the Master account is connected\n" +
        "• that the main Agent is active\n" +
        "• that the copier Agent is installed on the right account\n" +
        "• that the copier token is correct\n" +
        "• that algorithmic trading is enabled\n" +
        "• that WebRequest is still allowed\n" +
        "• that MetaTrader 5 is open\n" +
        "• that the chosen volume is accepted\n" +
        "• that the account has enough margin\n" +
        "• that the symbol exists at the copier account's broker",
    },
    {
      q: "Will the results be exactly identical?",
      a:
        "Not necessarily.\n\n" +
        "Differences can appear depending on:\n" +
        "• the spread\n" +
        "• slippage\n" +
        "• the broker\n" +
        "• the available symbol\n" +
        "• the configured volume\n" +
        "• connection speed\n" +
        "• the account or prop firm rules\n\n" +
        "Copy Trading forwards the positions, but never guarantees a perfectly identical result across accounts.",
    },
    {
      q: "What precautions should I take?",
      a:
        "Before enabling copying, always check:\n" +
        "• the right Master account\n" +
        "• the right copier accounts\n" +
        "• the tokens used\n" +
        "• the maximum lot allowed\n" +
        "• the available margin\n" +
        "• the rules of each broker or prop firm\n" +
        "• the connection state of every Agent\n\n" +
        "Start with a small volume to check the whole configuration works.",
    },
    {
      q: "How do I contact support?",
      a:
        "For a faster answer, we recommend contacting FullMargin support on WhatsApp.\n\n" +
        "If something goes wrong, send:\n" +
        "• the email address of your FullMargin account\n" +
        "• the Master account involved\n" +
        "• the copier accounts involved\n" +
        "• the names of the tokens used\n" +
        "• a screenshot of the Agents\n" +
        "• a screenshot of the Copy Trading configuration\n" +
        "• the symbol and time of the position that was not copied",
    },
  ],
  related: ["agent-ia-mt5", "execution", "autres"],
};
